// ParentGate — a discreet "for grown-ups" entry guarded by a 4-digit PIN
// (default 1234, changeable). Passing it opens the adult ParentDashboard. Styled
// in the same calm, non-game register as the dashboard itself.

import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ParentDashboard } from './ParentDashboard.js';

type Mode = 'closed' | 'pin' | 'open' | 'change';
const PIN_KEY = 'bizkid-parent-pin';
const FONT = "'Inter', system-ui, -apple-system, Segoe UI, sans-serif";

function readPin(): string {
  try {
    return localStorage.getItem(PIN_KEY) || '1234';
  } catch {
    return '1234';
  }
}
function writePin(pin: string) {
  try {
    localStorage.setItem(PIN_KEY, pin);
  } catch {
    /* ignore */
  }
}

export function ParentGate() {
  const [mode, setMode] = useState<Mode>('closed');

  return (
    <>
      {mode === 'closed' && (
        <button
          type="button"
          onClick={() => setMode('pin')}
          aria-label="For grown-ups"
          style={{
            position: 'fixed',
            bottom: 18,
            left: 18,
            zIndex: 50,
            padding: '8px 14px',
            background: 'rgba(255,255,255,0.6)',
            border: '1px solid rgba(60,30,15,0.18)',
            borderRadius: 999,
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            fontFamily: FONT,
            fontSize: 12,
            fontWeight: 700,
            color: '#5b6675',
            cursor: 'pointer',
          }}
        >
          For grown-ups
        </button>
      )}

      <AnimatePresence>
        {mode === 'pin' && <PinPad key="pin" title="Enter your PIN" verify onDone={(ok) => setMode(ok ? 'open' : 'pin')} onCancel={() => setMode('closed')} />}
        {mode === 'change' && (
          <PinPad
            key="change"
            title="Set a new PIN"
            onDone={(_, pin) => {
              if (pin) writePin(pin);
              setMode('open');
            }}
            onCancel={() => setMode('open')}
          />
        )}
        {mode === 'open' && <ParentDashboard key="dash" onClose={() => setMode('closed')} onChangePin={() => setMode('change')} />}
      </AnimatePresence>
    </>
  );
}

/** A 4-digit PIN entry. In `verify` mode it checks against the stored PIN; otherwise
 *  it returns the freshly-entered PIN to the caller (for changing it). */
function PinPad({ title, verify, onDone, onCancel }: { title: string; verify?: boolean; onDone: (ok: boolean, pin?: string) => void; onCancel: () => void }) {
  const [entry, setEntry] = useState('');
  const [wrong, setWrong] = useState(false);

  const press = (d: string) => {
    if (entry.length >= 4) return;
    const next = entry + d;
    setEntry(next);
    setWrong(false);
    if (next.length === 4) {
      setTimeout(() => {
        if (verify) {
          if (next === readPin()) onDone(true);
          else {
            setWrong(true);
            setEntry('');
          }
        } else {
          onDone(true, next);
        }
      }, 120);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onCancel}
      style={{ position: 'fixed', inset: 0, zIndex: 92, display: 'grid', placeItems: 'center', padding: 24, background: 'rgba(27,36,48,0.55)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', fontFamily: FONT }}
    >
      <motion.div
        initial={{ y: 14, scale: 0.97 }}
        animate={{ y: 0, scale: 1 }}
        onClick={(e) => e.stopPropagation()}
        style={{ width: '100%', maxWidth: 300, background: '#fff', borderRadius: 14, border: '1px solid #e1e5ea', padding: 24, textAlign: 'center', color: '#1b2430' }}
      >
        <div style={{ fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#9aa4b1', fontWeight: 600 }}>Grown-ups only</div>
        <h2 style={{ margin: '6px 0 16px', fontSize: 18, fontWeight: 700 }}>{title}</h2>
        {/* dots */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginBottom: 18 }}>
          {[0, 1, 2, 3].map((i) => (
            <span key={i} style={{ width: 12, height: 12, borderRadius: 999, background: i < entry.length ? '#2c6e6a' : '#dfe3e8', transition: 'background 0.15s' }} />
          ))}
        </div>
        {wrong && <p style={{ margin: '0 0 12px', fontSize: 13, color: '#b5302a' }}>Incorrect PIN. Try again.</p>}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
          {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
            <KeyBtn key={d} label={d} onClick={() => press(d)} />
          ))}
          <span />
          <KeyBtn label="0" onClick={() => press('0')} />
          <KeyBtn label="⌫" onClick={() => setEntry((e) => e.slice(0, -1))} subtle />
        </div>
        <button type="button" onClick={onCancel} style={{ marginTop: 16, background: 'transparent', border: 'none', color: '#9aa4b1', fontFamily: FONT, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          Cancel
        </button>
        {verify && <p style={{ margin: '10px 0 0', fontSize: 11, color: '#c0c7d0' }}>Default PIN is 1234</p>}
      </motion.div>
    </motion.div>
  );
}

function KeyBtn({ label, onClick, subtle }: { label: string; onClick: () => void; subtle?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ height: 52, borderRadius: 10, border: '1px solid #e6e9ee', background: subtle ? 'transparent' : '#f7f8fa', color: '#1b2430', fontFamily: FONT, fontSize: 20, fontWeight: 600, cursor: 'pointer' }}
    >
      {label}
    </button>
  );
}
